"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo, type ReactNode } from "react";
import { useAuth } from "@/context/authcontext";
import type { BreadcrumbPath } from "./breadcrumb.types";
import {
    buildBreadcrumbJsonLd,
    destinationKey,
    getSiteOrigin,
    hardNavigatePostLoginProductHome,
    isExternalHref,
    resolveHomeBreadcrumbPaths,
    shouldHardRefreshHomeBreadcrumb,
} from "./breadcrumb-utils";
import Script from "next/script";

export type { BreadcrumbPath } from "./breadcrumb.types";
export {
    getHomeBreadcrumbRoute,
    resolveHomeBreadcrumbPaths,
} from "./breadcrumb-utils";

const textBase =
    "text-xs font-semibold leading-[19.2px] tracking-[0.12px] md:text-md md:leading-[20.8px]";

interface BreadcrumbClientProps {
    paths: BreadcrumbPath[];
    /** Overrides inferred URL for JSON-LD when the last crumb has no `route`/`href`. */
    currentPageAbsoluteUrl?: string;
}

const BreadcrumbClient = ({ paths, currentPageAbsoluteUrl: currentPageProp }: BreadcrumbClientProps) => {
    const pathname = usePathname();
    const { isAuthenticated } = useAuth();

    const resolvedPaths = useMemo(
        () =>
            resolveHomeBreadcrumbPaths(paths, Boolean(isAuthenticated), {
                pathname,
            }),
        [paths, isAuthenticated, pathname],
    );

    const hardRefreshHome = shouldHardRefreshHomeBreadcrumb(
        Boolean(isAuthenticated),
        pathname,
    );

    const siteOrigin = getSiteOrigin();
    const inferredCurrentUrl = `${siteOrigin}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
    const currentPageAbsoluteUrl = currentPageProp ?? inferredCurrentUrl;

    const jsonLd = useMemo(
        () =>
            buildBreadcrumbJsonLd(resolvedPaths, {
                siteOrigin,
                currentPageAbsoluteUrl,
            }),
        [resolvedPaths, siteOrigin, currentPageAbsoluteUrl],
    );

    return (
        <nav aria-label="Breadcrumb" className="relative mb-0 w-full py-[10px] md:mb-8">
            <ol className="m-0 flex list-none flex-wrap items-center p-0">
                {resolvedPaths.map((path, index) => {
                    const isLast = index === resolvedPaths.length - 1;
                    const dest = destinationKey(path);
                    const hasOnClick = typeof path.onClick === "function";
                    const isHomeHardRefresh =
                        !isLast && hardRefreshHome && path.label === "Home";
                    const isClickable = hasOnClick || !!dest || isHomeHardRefresh;
                    const className = `${textBase} ${
                        isLast
                            ? "text-orange-500"
                            : isClickable
                              ? "cursor-pointer text-neutral-300 underline-offset-2 hover:underline"
                              : "text-neutral-900"
                    }`;

                    let inner: ReactNode;

                    if (!isLast && isHomeHardRefresh) {
                        inner = (
                            <button
                                type="button"
                                className={`${className} bg-transparent p-0 text-left`}
                                onClick={() => hardNavigatePostLoginProductHome(pathname)}
                            >
                                {path.label}
                            </button>
                        );
                    } else if (!isLast && hasOnClick && !dest) {
                        inner = (
                            <button
                                type="button"
                                className={`${className} bg-transparent p-0 text-left`}
                                onClick={() => path.onClick?.()}
                            >
                                {path.label}
                            </button>
                        );
                    } else if (!isLast && dest) {
                        if (isExternalHref(dest)) {
                            inner = (
                                <a href={dest} className={className}>
                                    {path.label}
                                </a>
                            );
                        } else {
                            inner = (
                                <Link href={dest} className={className}>
                                    {path.label}
                                </Link>
                            );
                        }
                    } else {
                        inner = (
                            <span className={className} {...(isLast ? { "aria-current": "page" as const } : {})}>
                                {path.label}
                            </span>
                        );
                    }

                    return (
                        <li key={index} className="flex items-center">
                            {inner}
                            {index < resolvedPaths.length - 1 && (
                                <span
                                    className="mx-1 inline-flex items-center leading-[19.2px] text-neutral-900 md:leading-[20.8px]"
                                    aria-hidden
                                >
                                    /
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
            <Script
                id="breadcrumb-schema"
                type="application/ld+json"
                strategy="afterInteractive"
                // eslint-disable-next-line react/no-danger -- JSON-LD must be inline for crawlers
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
            />
        </nav>
    );
};

export default BreadcrumbClient;
